
import { onMounted, useContext, useRoute, useRouter } from '@nuxtjs/composition-api';
import useCategories from '@/composable/useCategories';
import useProducts from '@/composable/useProducts';
import useArticleCards from '@/composable/contentful/useArtcleCards';
import useSearch from '@/composable/useSearch';
import usePageScroll from '@/composable/usePageScroll';
import useTracking from '@/composable/tracking/useTracking';

const formatQueryObject = (prefix, queryParams) =>
  Object.keys(queryParams).reduce((prev, cur) => {
    if (cur.includes(prefix) && queryParams[cur]) {
      const key = cur.replace(prefix, '')
      prev[key] = queryParams[cur].split ? queryParams[cur].split(',') : queryParams[cur] || []
    }
    return prev
  }, {})

const handleSearchFilters = (prefix, queryParams, cb) => {
  if (!Object.keys(queryParams || {}).length && !process.server) { return }
  const queryObject = formatQueryObject(prefix, queryParams)
  return cb(queryObject) || queryObject
}

export default {
  name: 'ProductsPage',
  beforeRouteLeave (_to, _from, next) {
    const { storePageScrollPosition } = usePageScroll()
    storePageScrollPosition()
    next()
  },
  setup() {
    const app = useContext()
    const { search: doSearch, state, setSearchFilters, setQueryParams } = useSearch(app)
    const { track } = useTracking()
    const router = useRouter()
    const route = useRoute()
    const queryParamSetter = setQueryParams(router, route)


    onMounted(() => {
      const { restorePageScrollPosition } = usePageScroll()
      restorePageScrollPosition()
      $nuxt.$on('filters-changed', () => {
        queryParamSetter()
      })
    })

    track('loadProductListPage', { category: route.value.params.categoryslug })

    handleSearchFilters('filter-', route.value.query, setSearchFilters)

    return {
      state,
      doSearch,
      track
    }
  },
  // eslint-disable-next-line complexity
  async asyncData (ctx) {
    const { params, $hubble, error, route } = ctx
    const { search: doSearch, setSearchFilters } = useSearch(ctx)
    const categorySlug = params.categoryslug ? params.categoryslug.toLowerCase() : null;
    const { getProductsByCategoryID } = useProducts($hubble);


    const {
      getCategoryBySlug,
      getCategoryTree,
      getCategoryHeader,
      title,
      description,
      metaTitle,
      metaDescription,
      articleCards,
      topBanner,
      categoryFaQs,
      getHasSpecialPrices,
      hasSpecialPrices
    } = useCategories($hubble);
    let category = null;
    const { articleIndexList } = useArticleCards(articleCards)

    if (categorySlug !== null && categorySlug !== undefined && categorySlug !== 'undefined' && categorySlug !== '') {
      category = await getCategoryBySlug(categorySlug);
      if (!category?.id) {
        if (error) {
          if (process.server) {
            console.error('Slug category parameter could not be found in commerce, cant list products, page can not be found. Slug from url is:', categorySlug)
          }
          error({ statusCode: 404, message: 'Page not found.' })
          return false
        }
        throw new Error('Category not found')
      }
    }
    const [categoryTree, productListFamily] = await Promise.all([
      getCategoryTree(category.id),
      getProductsByCategoryID(category.id, !process.browser),
      getCategoryHeader(categorySlug),
      getHasSpecialPrices({ categorySlug })
    ])


    handleSearchFilters('filter-', route.query, setSearchFilters)

    const searchResult = doSearch(productListFamily, categorySlug, categoryTree)

    return {
      topBanner: topBanner.value,
      hasSpecialPrices: hasSpecialPrices.value,
      productListFamily,
      categoryHeader: title.value,
      categoryDescription: description.value,
      categorySlug,
      categoryFaQs: categoryFaQs.value,
      metaTitle: metaTitle.value,
      metaDescription: metaDescription.value,
      articleCards: articleCards.value,
      articleIndexList: articleIndexList.value,
      categoryTree,
      loading: false,
      searchResult
    }
  },
  data () {
    return {
      searchResult: {}
    }
  },
  head () {
    if (this.hasSpecialPrices) {
      return {
        title: this.metaTitle,
        meta: [
          {
            hid: 'robots',
            name: 'robots',
            content: 'noindex, nofollow'
          }
        ]
      }
    }

    if (!this.metaTitle) {
      return false
    }

    return {
      title: this.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.metaDescription
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.metaDescription
        }
      ]
    }
  },
  mounted() {
    this.$nuxt.$on('filters-changed', () => {
      this.searchResult = this.doSearch(this.productListFamily, this.categorySlug, this.categoryTree)

      // scroll to top on filter change NLOS-2727
      const element = document.querySelector('main')
      if (!element) { return }
      const bodyTop = document.body.getBoundingClientRect().top
      const elTop = element.getBoundingClientRect().top
      const offset = elTop - bodyTop
      scrollTo({ top: offset + 150, behavior: 'smooth' });
    })
  }
}
